import {Button} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {getNullableStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import AnalysisComponentErrorBoundary from 'analysis-tool/ui/components/AnalysisComponentErrorBoundary';
import {AnalysisMainViewItem} from 'analysis-tool/ui/main-view/MainViewTypes';

import CombineTablesSourceRightSidebar from './combine-data-frames/CombineTablesSourceRightSidebar';
import AddStepRightSidebar from './components/add-step/AddStepRightSidebar';
import {AnalysisEditStepRightSidebar} from './components/EditStepRightSidebar';
import EditViewRightSidebar from './components/EditViewRightSidebar';
import ThirdPartyImportRightSidebar from './components/ThirdPartyImportRightSidebar';
import {
  AnalysisRightSidebarItemType,
  GenericAnalysisRightSidebarItem,
  RightSidebarComponentProps,
} from './RightSidebarTypes';

const ITEM_TYPE_TO_RIGHT_SIDEBAR_COMPONENT: {
  [key in AnalysisRightSidebarItemType]: React.FC<RightSidebarComponentProps<key>>;
} = {
  [AnalysisRightSidebarItemType.ADD_STEP]: AddStepRightSidebar,
  [AnalysisRightSidebarItemType.ADD_SOURCE_STEP]: AddStepRightSidebar,
  [AnalysisRightSidebarItemType.EDIT_STEP]: AnalysisEditStepRightSidebar,
  [AnalysisRightSidebarItemType.EDIT_VIEW]: EditViewRightSidebar,
  [AnalysisRightSidebarItemType.THIRD_PARTY_IMPORT]: ThirdPartyImportRightSidebar,
  [AnalysisRightSidebarItemType.COMBINE_TABLES_SOURCE]: CombineTablesSourceRightSidebar,
};

export type AnalysisUISidebarRouterProps = Omit<
  RightSidebarComponentProps<AnalysisRightSidebarItemType>,
  'writable' | 'selectedRightSidebarItem'
> & {
  //  Specify the right side bar item being rendered. `null` means no right side bar is selected.
  selectedRightSidebarItem: GenericAnalysisRightSidebarItem<AnalysisRightSidebarItemType> | null;
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem | null) => void;
};

/** Route for `AnalysisRightSidebarItemType`s to a component to render in the `AnalysisUISidebar`.
 *
 * We pass down `analysis`, `selectedRightSidebarItem`, and shared props
 */
export default function AnalysisRightSidebarRouter({
  analysis,
  selectedRightSidebarItem,
  ...restProps
}: AnalysisUISidebarRouterProps): JSX.Element | null {
  if (selectedRightSidebarItem == null) {
    return null;
  }

  if (
    // eslint-disable-next-line benchlint/no-in-operator
    'isUpdating' in selectedRightSidebarItem &&
    selectedRightSidebarItem.isUpdating
  ) {
    return null;
  }

  // Steps can be deleted from the flowchart while the sidebar is open; simply close the sidebar if the step
  // no longer exists.
  if (
    // eslint-disable-next-line benchlint/no-in-operator
    'stepId' in selectedRightSidebarItem &&
    selectedRightSidebarItem.stepId != null &&
    getNullableStepFromAnalysis(analysis, selectedRightSidebarItem.stepId) == null
  ) {
    return null;
  }

  const ViewComponent: React.FC<RightSidebarComponentProps<AnalysisRightSidebarItemType>> =
    ITEM_TYPE_TO_RIGHT_SIDEBAR_COMPONENT[selectedRightSidebarItem.type];
  return (
    <AnalysisComponentErrorBoundary
      errorExtras={{analysisId: analysis.id, selectedRightSidebarItem}}
      actionContent={
        <Button
          onClick={() => {
            restProps.onChangeSelectedRightSidebarItem(null);
          }}
        >
          Close sidebar
        </Button>
      }
      className={css(styles.errorBoundaryWrapper)}
    >
      <ViewComponent {...restProps} analysis={analysis} selectedRightSidebarItem={selectedRightSidebarItem} />
    </AnalysisComponentErrorBoundary>
  );
}

const styles = StyleSheet.create({
  errorBoundaryWrapper: {
    maxWidth: '33%',
  },
});
